import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tourApi from "../api/tour";
import Loader from "../components/Loader";
import SEO from "../components/SEO";
import { useBooking } from "../context/BookingContext";
import { MapPin, Calendar, Star } from "lucide-react";
import reviewApi from "../api/review";
import { useAuth } from "../context/AuthContext";
import AlertMessage from "../components/AlertMessage";
import { getImageUrl } from "../api/axios";
import ImageGallery from "../components/ImageGallery";
import { useSettings } from "../context/SettingsContext";

export default function TourDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSelectedTour } = useBooking();
  const { settings } = useSettings();
  const cs = settings.currency_symbol || "$";
  const discount = Number(settings.discount_percentage) || 0;
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    comment: "",
  });
  const [reviewError, setReviewError] = useState("");
  const [reviewSuccess, setReviewSuccess] = useState("");
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await tourApi.getTourDetails(id);
        if (active) setTour(data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await reviewApi.listReviews(id);
        if (active) setReviews(data);
      } catch (err) {
        setReviewError(
          err.response?.data?.message || "Failed to load reviews."
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const handleReviewChange = (e) => {
    setReviewForm({ ...reviewForm, [e.target.name]: e.target.value });
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setReviewError("");
    setReviewSuccess("");
    setIsReviewLoading(true);
    try {
      const { data: res } = await reviewApi.createReview({
        ...reviewForm,
        tour_id: id,
      });

      // Transformer le nouvel avis pour qu'il ait user et autres champs
      const newReview = {
        ...res.data,
        rating: Number(res.data.rating), // assure un number
        user: res.data.user || { id: 0, name: "Utilisateur inconnu" },
      };

      setReviews((prev) => [newReview, ...prev]);
      setReviewForm({ rating: 0, comment: "" });
      setReviewSuccess("Review added successfully!");
    } catch (err) {
      setReviewError(err.response?.data?.message || "Failed to add review.");
    } finally {
      setIsReviewLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!tour) return <div>Tour not found.</div>;

  return (
    <div className="flex flex-col gap-8">
      <SEO
        title={tour.title}
        description={tour.description?.substring(0, 160) || `Discover ${tour.title} - an unforgettable Morocco tour experience with Moroccan Desert Trek.`}
        path={`/tours/${id}`}
        image={getImageUrl(tour.thumbnail)}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TouristTrip",
          "name": tour.title,
          "description": tour.description,
          "touristType": "Adventure",
          "provider": {
            "@type": "TravelAgency",
            "name": "Moroccan Desert Trek",
            "url": "https://moroccandeserttrek.com"
          },
          ...(tour.price && {
            "offers": {
              "@type": "Offer",
              "price": tour.price,
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            }
          }),
          ...(reviews.length > 0 && {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": (reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length).toFixed(1),
              "reviewCount": reviews.length
            }
          })
        }}
      />
      {/* Header Image with Title Overlay */}
      <div className="relative h-72 md:h-96 overflow-hidden rounded-2xl shadow-md">
        <img
          src={getImageUrl(tour.thumbnail)}
          alt={tour.title}
          loading="lazy"
          className="h-full w-full object-cover"
        />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white bg-black/30 backdrop-blur-sm">
          <h1 className="text-4xl font-extrabold drop-shadow-lg">
            {tour.title}
          </h1>
          <p className="flex items-center gap-3 mt-2 text-lg text-gray-200">
            <span className="flex items-center gap-1">
              <MapPin size={18} /> {tour.city}
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={18} /> {tour.durationDays} days
            </span>
            <span className="flex items-center gap-1">
              <Star className="text-yellow-400" size={18} /> {tour.rating}
            </span>
          </p>
          {tour.start_date && (
            <p className="mt-2 text-sm text-gray-200 bg-black/20 px-3 py-1 rounded-full inline-block">
              Departure: {new Date(tour.start_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}
        </div>
      </div>

      {/* Additional Images Gallery (clickable thumbnails) */}
      <ImageGallery
        images={tour.images?.map(img => getImageUrl(img)) || []}
        tourTitle={tour.title}
      />

      <div className="flex flex-col md:flex-row gap-6">
        {/* Tour Overview */}
        <div className="flex-1 space-y-6">
          <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
            Tour Overview
          </h2>
          <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
            {tour.summary}
          </p>
        </div>

        {/* 💰 Carte de réservation */}
        <aside className="w-full md:w-80 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-500 text-white shadow-lg p-6 flex flex-col justify-between">
          <div>
            {discount > 0 ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg line-through text-emerald-200/70">{cs}{tour.price}</span>
                  <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">-{discount}%</span>
                </div>
                <h3 className="text-3xl font-bold mb-2">{cs}{(tour.price * (1 - discount / 100)).toFixed(2)}</h3>
              </>
            ) : (
              <h3 className="text-3xl font-bold mb-2">{cs}{tour.price}</h3>
            )}
            <p className="text-sm text-emerald-100">
              per person
            </p>
          </div>
          {(() => {
            // ✅ Vérifier si le tour est dans le passé
            const isPastTour = tour.start_date &&
              new Date(tour.start_date.split('T')[0]) < new Date(new Date().toISOString().split('T')[0]);

            if (isPastTour) {
              return (
                <div className="mt-6 w-full rounded-lg bg-red-500/80 text-white font-medium py-2 px-4 text-center">
                  <p className="text-sm">This tour has already started</p>
                  <p className="text-xs mt-1 opacity-90">No longer available for booking</p>
                </div>
              );
            }

            return (
              <button
                onClick={() => {
                  setSelectedTour(tour);
                  navigate("/booking");
                }}
                className="mt-6 w-full rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium py-2 transition-all"
              >
                Book Now
              </button>
            );
          })()}
        </aside>
      </div>

      {/* 📜 Itinéraire */}
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
          Itinerary
        </h2>
        <ol className="space-y-3">
          {(() => {
            // ✅ L'itinéraire est maintenant un JSON array: [{day: 1, description: "..."}, ...]
            const itinerary = Array.isArray(tour.itinerary) ? tour.itinerary : [];

            if (itinerary.length === 0) {
              return (
                <p className="text-slate-500 dark:text-slate-400">
                  No itinerary available for this tour.
                </p>
              );
            }

            return itinerary.map((step, i) => {
              const dayNumber = step.day || i + 1;
              const title = step.title || "";
              const description = step.description || "";
              const highlights = Array.isArray(step.highlights) ? step.highlights : [];

              return (
                <li
                  key={i}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white/50 dark:bg-slate-800/50 shadow-sm"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 shrink-0">
                      {dayNumber}
                    </span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {title || `Day ${dayNumber}`}
                    </span>
                  </div>
                  {description && (
                    <p className="text-slate-600 dark:text-slate-300 text-sm ml-11">
                      {description}
                    </p>
                  )}
                  {highlights.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 ml-11">
                      {highlights.map((hl, hlIdx) => (
                        <span
                          key={hlIdx}
                          className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        >
                          {hl}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              );
            });
          })()}
        </ol>
      </section>
      {/* Reviews Section */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
            Traveler Reviews
          </h2>
          {reviews.length > 0 && (
            <span className="text-sm text-slate-500 dark:text-slate-400">{reviews.length} review{reviews.length > 1 ? "s" : ""}</span>
          )}
        </div>

        {/* Review Summary Stats */}
        {reviews.length > 0 && (() => {
          const avg = reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / reviews.length;
          const counts = [5, 4, 3, 2, 1].map(n => ({ star: n, count: reviews.filter(r => Number(r.rating) === n).length }));
          return (
            <div className="flex flex-col sm:flex-row gap-6 rounded-xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/50">
              <div className="flex flex-col items-center justify-center min-w-[100px]">
                <span className="text-4xl font-bold text-slate-900 dark:text-white">{avg.toFixed(1)}</span>
                <div className="flex mt-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} size={16} className={s <= Math.round(avg) ? "text-yellow-400 fill-yellow-400" : "text-slate-300 dark:text-slate-600"} />
                  ))}
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">{reviews.length} reviews</span>
              </div>
              <div className="flex-1 space-y-1.5">
                {counts.map(({ star, count }) => (
                  <div key={star} className="flex items-center gap-2 text-sm">
                    <span className="w-6 text-right text-slate-600 dark:text-slate-400 font-medium">{star}</span>
                    <Star size={12} className="text-yellow-400 fill-yellow-400" />
                    <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-400 rounded-full transition-all" style={{ width: `${reviews.length > 0 ? (count / reviews.length) * 100 : 0}%` }} />
                    </div>
                    <span className="w-8 text-xs text-slate-500 dark:text-slate-400">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {reviewError && <AlertMessage type="error">{reviewError}</AlertMessage>}
        {reviewSuccess && <AlertMessage type="success">{reviewSuccess}</AlertMessage>}

        {/* Write a Review */}
        {user ? (
          <form
            onSubmit={handleSubmitReview}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              Write a Review
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Your Rating
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: s })}
                    className="p-1 transition-transform hover:scale-125"
                  >
                    <Star
                      size={28}
                      className={`transition-colors ${
                        s <= reviewForm.rating
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-slate-300 dark:text-slate-600 hover:text-yellow-300"
                      }`}
                    />
                  </button>
                ))}
                {reviewForm.rating > 0 && (
                  <span className="ml-2 text-sm text-slate-500 dark:text-slate-400 self-center">
                    {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][reviewForm.rating]}
                  </span>
                )}
              </div>
              <input type="hidden" name="rating" value={reviewForm.rating} required />
            </div>
            <div className="mb-4">
              <label
                htmlFor="comment"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                Your Experience (optional)
              </label>
              <textarea
                id="comment"
                name="comment"
                value={reviewForm.comment}
                onChange={handleReviewChange}
                rows="3"
                placeholder="Tell others about your experience on this tour..."
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={isReviewLoading || reviewForm.rating === 0}
              className="rounded-lg bg-emerald-600 px-6 py-2.5 font-medium text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isReviewLoading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Submitting...
                </>
              ) : (
                "Submit Review"
              )}
            </button>
          </form>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-800/50">
            <Star size={24} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <a href="/login" className="text-emerald-600 hover:underline font-medium">Log in</a> to share your experience
            </p>
          </div>
        )}

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="text-center py-8">
            <Star size={32} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No reviews yet. Be the first to review this tour!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => {
              const name = review.user?.name || "Anonymous";
              const initials = name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
              const rating = Number(review.rating) || 0;
              const timeAgo = review.created_at ? (() => {
                const diff = Date.now() - new Date(review.created_at).getTime();
                const days = Math.floor(diff / 86400000);
                if (days === 0) return "Today";
                if (days === 1) return "Yesterday";
                if (days < 30) return `${days} days ago`;
                if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? "s" : ""} ago`;
                return new Date(review.created_at).toLocaleDateString();
              })() : "";
              return (
                <div
                  key={review.id}
                  className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-sm font-bold">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                          {name}
                        </p>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{timeAgo}</span>
                      </div>
                      <div className="flex mt-0.5 mb-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} size={14} className={s <= rating ? "text-yellow-400 fill-yellow-400" : "text-slate-300 dark:text-slate-600"} />
                        ))}
                      </div>
                      {review.comment && (
                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                          {review.comment}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
